import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

export const ScoreCircle = ({ score, size = 200, strokeWidth = 12 }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const offset = circumference - progress;

  const getScoreColor = (score) => {
    if (score < 50) return '#EF4444'; // red
    if (score < 70) return '#F59E0B'; // amber
    if (score < 85) return '#0EA5E9'; // sky blue
    return '#10B981'; // emerald
  };

  const color = useMemo(() => getScoreColor(score), [score]);

  return (
    <div className="score-display" style={{ width: size, height: size }}>
      <svg
        width={size}
        height={size}
        className="score-ring"
      >
        <circle
          className="bg"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke="#E2E8F0"
        />
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          stroke={color}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.5, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <motion.span 
          className="text-5xl font-bold text-slate-900 tabular-nums font-heading"
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          {score}
        </motion.span>
        <span className="text-sm text-slate-500 font-medium">out of 100</span>
      </div>
    </div>
  );
};

export const ScoreCategory = ({ category }) => {
  const getCategoryStyle = (cat) => {
    switch (cat) {
      case 'Strong Lending Candidate':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      case 'Funding Ready':
        return 'bg-sky-100 text-sky-800 border-sky-200';
      case 'Preparation Required':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      default:
        return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  return (
    <motion.span 
      className={`inline-flex px-4 py-2 rounded-full text-sm font-semibold border ${getCategoryStyle(category)}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.8 }}
    >
      {category}
    </motion.span>
  );
};

export const SectionBar = ({ name, score, maxScore, delay = 0 }) => {
  const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
  
  const getBarColor = (pct) => {
    if (pct < 40) return 'bg-red-500';
    if (pct < 60) return 'bg-amber-500';
    if (pct < 80) return 'bg-sky-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{name}</span>
        <span className="text-sm font-semibold text-slate-900 tabular-nums">
          {score}/{maxScore}
        </span>
      </div>
      <div className="section-bar">
        <motion.div 
          className={`section-bar-fill ${getBarColor(percentage)}`}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ delay, duration: 0.8, ease: "easeOut" }}
        />
      </div>
    </div>
  );
};

export default ScoreCircle;
